/**
 * Sector operations — Stays room states client service.
 * R39 K2.a + K2.b + K3.a + K3.b (plan docs/plans/sector-operations.md §Part K, SO-P113, SO-D201, SO-D206, SO-D207).
 */

import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type { OpsSubscriptionAccessOptions } from './opsRostersService'
import type {
  OpsRoomState,
  OpsRoomEvent,
  CheckOutRoomInput,
  MarkRoomOccupiedInput,
  SetRoomDueOutInput,
  StartRoomCleanInput,
  MarkRoomCleanedInput,
  PauseRoomCleanInput,
  ResumeRoomCleanInput,
  StaysHousekeepingReportInput,
  StaysHousekeepingReportResult,
  InspectRoomInput,
  SetRoomServiceInput,
  SetRoomTagsInput,
  SetPropertyMinibarCheckInput,
  SetPropertyMinibarCheckResult,
  PrepareRoomCleanRecordInput,
  PrepareRoomCleanRecordResult,
  TransitionRoomStateResultPayload,
  AssignRoomsInput,
  UnassignRoomInput,
  ReassignRoomInput,
} from '../types/ops'

// ---------------------------------------------------------------------------
// 1. Subscriptions & Direct Reads
// ---------------------------------------------------------------------------

export function subscribeToRoomStates(
  workspaceId: string,
  propertyId: string | null | undefined,
  callback: (states: OpsRoomState[]) => void,
  onError?: (error: Error) => void,
  accessScope?: OpsSubscriptionAccessOptions,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const roomStatesRef = collection(firestoreDb, 'ops_room_states')

  if (accessScope?.isRestricted === true) {
    const callerUid = accessScope.callerUid?.trim()
    if (!callerUid) {
      onError?.(new Error('callerUid is required for restricted subscribeToRoomStates'))
      return () => undefined
    }

    const qNull = propertyId
      ? query(
          roomStatesRef,
          where('workspaceId', '==', workspaceId),
          where('propertyId', '==', propertyId),
          where('readAccess', '==', null),
        )
      : query(
          roomStatesRef,
          where('workspaceId', '==', workspaceId),
          where('readAccess', '==', null),
        )

    const qUid = propertyId
      ? query(
          roomStatesRef,
          where('workspaceId', '==', workspaceId),
          where('propertyId', '==', propertyId),
          where('readAccess', 'array-contains', callerUid),
        )
      : query(
          roomStatesRef,
          where('workspaceId', '==', workspaceId),
          where('readAccess', 'array-contains', callerUid),
        )

    let statesNull: OpsRoomState[] = []
    let statesUid: OpsRoomState[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, OpsRoomState>()
      for (const s of statesNull) {
        if (s.placeId) map.set(s.placeId, s)
      }
      for (const s of statesUid) {
        if (s.placeId) map.set(s.placeId, s)
      }
      callback(Array.from(map.values()))
    }

    const unsubNull = onSnapshot(
      qNull,
      (snap) => {
        statesNull = snap.docs.map((d) => ({ ...d.data(), placeId: d.id }) as OpsRoomState)
        mergeAndEmit()
      },
      (err) => onError?.(err),
    )

    const unsubUid = onSnapshot(
      qUid,
      (snap) => {
        statesUid = snap.docs.map((d) => ({ ...d.data(), placeId: d.id }) as OpsRoomState)
        mergeAndEmit()
      },
      (err) => onError?.(err),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const q = propertyId
    ? query(
        roomStatesRef,
        where('workspaceId', '==', workspaceId),
        where('propertyId', '==', propertyId),
      )
    : query(roomStatesRef, where('workspaceId', '==', workspaceId))

  return onSnapshot(
    q,
    (snap) => {
      const states = snap.docs.map((d) => ({ ...d.data(), placeId: d.id }) as OpsRoomState)
      callback(states)
    },
    (err) => onError?.(err),
  )
}

export function subscribeToRoomEvents(
  roomId: string,
  callback: (events: OpsRoomEvent[]) => void,
  onError?: (error: Error) => void,
  maxEvents: number = 50,
): Unsubscribe {
  if (!roomId) {
    callback([])
    return () => undefined
  }

  const eventsRef = collection(firestoreDb, 'ops_room_states', roomId, 'events')
  const q = query(eventsRef, orderBy('at', 'desc'), limit(maxEvents))

  return onSnapshot(
    q,
    (snap) => {
      const events = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsRoomEvent)
      callback(events)
    },
    (err) => onError?.(err),
  )
}

export async function getRoomState(roomId: string): Promise<OpsRoomState | null> {
  if (!roomId) return null
  const snap = await getDoc(doc(firestoreDb, 'ops_room_states', roomId))
  if (!snap.exists()) return null
  return { ...snap.data(), placeId: snap.id } as OpsRoomState
}

// ---------------------------------------------------------------------------
// 2. Callables
// ---------------------------------------------------------------------------

export async function checkOutRoom(
  input: CheckOutRoomInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<CheckOutRoomInput, TransitionRoomStateResultPayload>('checkOutRoom', input)
}

export async function markRoomOccupied(
  input: MarkRoomOccupiedInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<MarkRoomOccupiedInput, TransitionRoomStateResultPayload>('markRoomOccupied', input)
}

export async function setRoomDueOut(
  input: SetRoomDueOutInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<SetRoomDueOutInput, TransitionRoomStateResultPayload>('setRoomDueOut', input)
}

export async function startRoomClean(
  input: StartRoomCleanInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<StartRoomCleanInput, TransitionRoomStateResultPayload>('startRoomClean', input)
}

export async function pauseRoomClean(
  input: PauseRoomCleanInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<PauseRoomCleanInput, TransitionRoomStateResultPayload>('pauseRoomClean', input)
}

export async function resumeRoomClean(
  input: ResumeRoomCleanInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<ResumeRoomCleanInput, TransitionRoomStateResultPayload>('resumeRoomClean', input)
}

export async function markRoomCleaned(
  input: MarkRoomCleanedInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<MarkRoomCleanedInput, TransitionRoomStateResultPayload>('markRoomCleaned', input)
}

export async function inspectRoom(
  input: InspectRoomInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<InspectRoomInput, TransitionRoomStateResultPayload>('inspectRoom', input)
}

export async function setRoomService(
  input: SetRoomServiceInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<SetRoomServiceInput, TransitionRoomStateResultPayload>('setRoomService', input)
}

export async function setRoomTags(
  input: SetRoomTagsInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<SetRoomTagsInput, TransitionRoomStateResultPayload>('setRoomTags', input)
}

export async function setPropertyMinibarCheck(
  input: SetPropertyMinibarCheckInput,
): Promise<SetPropertyMinibarCheckResult> {
  return callable<SetPropertyMinibarCheckInput, SetPropertyMinibarCheckResult>(
    'setPropertyMinibarCheck',
    input,
  )
}

export async function prepareRoomCleanRecord(
  input: PrepareRoomCleanRecordInput,
): Promise<PrepareRoomCleanRecordResult> {
  return callable<PrepareRoomCleanRecordInput, PrepareRoomCleanRecordResult>(
    'prepareRoomCleanRecord',
    input,
  )
}

export async function getStaysHousekeepingReport(
  input: StaysHousekeepingReportInput,
): Promise<StaysHousekeepingReportResult> {
  return callable<StaysHousekeepingReportInput, StaysHousekeepingReportResult>(
    'getStaysHousekeepingReport',
    input,
  )
}

export async function assignRooms(
  input: AssignRoomsInput,
): Promise<{ success: true; count: number }> {
  return callable<AssignRoomsInput, { success: true; count: number }>('assignRooms', input)
}

export async function unassignRoom(
  input: UnassignRoomInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<UnassignRoomInput, TransitionRoomStateResultPayload>('unassignRoom', input)
}

export async function reassignRoom(
  input: ReassignRoomInput,
): Promise<TransitionRoomStateResultPayload> {
  return callable<ReassignRoomInput, TransitionRoomStateResultPayload>('reassignRoom', input)
}
